import {useMessage} from '@coolcolduk/ui-mui';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router';
import {z} from 'zod';
import {QuestionStatus, User, UserPermission} from '@cp/model';
import useQuestionFilters from '../../../components/QuestionFilters/useQuestionFilters';
import {env} from '../../../config/env';
import {useSearchParamsHandler} from '../../../hooks/helper/useSearchParamsHandler';
import useListExamModules from '../../../hooks/examModules/useListExamModules';
import {useChangeQuestionStatus} from '../../../hooks/question/useChangeQuestionStatus';
import {useCountQuestions} from '../../../hooks/question/useCountQuestions';
import {useFetchQuestionForReview} from '../../../hooks/question/useFetchQuestionForReview';
import {useFlagQuestionAsError} from '../../../hooks/question/useFlagQuestionAsError';
import {useFlagQuestionAsOutOfScope} from '../../../hooks/question/useFlagQuestionAsOutOfScope';

const questionReviewParamsSchema = z.object({
  skip: z.number().int().min(0).optional(),
});

type QuestionReviewParams = z.infer<typeof questionReviewParamsSchema>;

export default function useAdminQuestionReviewQuestion(user: User) {
  const {examModules, loading: modulesLoading} = useListExamModules();
  const {questionId} = useParams<{questionId: string}>();
  const location = useLocation();
  const navigate = useNavigate();
  const {showMessage} = useMessage();

  const {fetchQuestionForReview, question, loading: fetchLoading} = useFetchQuestionForReview();
  const {changeQuestionStatus, loading: changeStatusLoading} = useChangeQuestionStatus();
  const {flagQuestionAsError, loading: flagErrorLoading} = useFlagQuestionAsError();
  const {flagQuestionAsOutOfScope, loading: flagOutOfScopeLoading} = useFlagQuestionAsOutOfScope();
  const {countQuestions, loading: countLoading} = useCountQuestions();

  const updateLoading = changeStatusLoading || flagErrorLoading || flagOutOfScopeLoading;

  const isAdmin = Boolean(user.roles?.includes(UserPermission.ADMIN));
  const isReviewer = Boolean(user.roles?.includes(UserPermission.REVIEWER));

  const {searchParams, setSearchParam} = useSearchParamsHandler<QuestionReviewParams>({
    schema: questionReviewParamsSchema,
    defaultValues: {
      skip: 0,
    },
  });

  const skipCount = searchParams.skip ?? 0;

  const {filters, setFilters} = useQuestionFilters({
    examModules,
    modulesLoading,
    statusAllowAll: isAdmin,
    statusAvailableStatuses: isReviewer && !isAdmin ? [QuestionStatus.VALIDATED] : Object.values(QuestionStatus),
    statusDisabled: (isReviewer && !isAdmin) || fetchLoading,
    useUrlParams: true, // Enable URL params persistence
  });

  // Set initial filters for reviewers if not in URL
  useEffect(() => {
    if (isReviewer && !isAdmin && !filters.status) {
      setFilters({
        status: QuestionStatus.VALIDATED,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [questionCount, setQuestionCount] = useState<number | null>(null);
  const [previousQuestionId, setPreviousQuestionId] = useState<string | undefined>(questionId);
  const isFetchingRef = useRef(false);
  const fetchedQuestionIdRef = useRef<string | undefined>(undefined);

  const currentQuestion = question;

  // Reset answer and feedback when question changes (e.g., when going back)
  useEffect(() => {
    if (questionId !== previousQuestionId) {
      setSelectedAnswer('');
      setShowFeedback(false);
      setPreviousQuestionId(questionId);
      // Reset the fetched question ref when questionId changes
      fetchedQuestionIdRef.current = undefined;
    }
  }, [questionId, previousQuestionId]);

  // Reset skip count when filters change
  useEffect(() => {
    setSearchParam('skip', 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.moduleId, filters.topic, filters.difficulty, filters.status]);

  useEffect(() => {
    let cancelled = false;

    if (filters.moduleId && questionId) {
      // If questionId is 'question', fetch a new question and update URL
      if (questionId === env.questionReview.noQuestionIs) {
        // Skip fetch if already fetching or if we've already fetched for this questionId
        if (isFetchingRef.current || fetchedQuestionIdRef.current === questionId) {
          return;
        }

        isFetchingRef.current = true;
        fetchedQuestionIdRef.current = questionId;
        // Reset skip to 0 when fetching first question (only if not already 0)
        if (skipCount !== 0) {
          setSearchParam('skip', 0);
        }
        fetchQuestionForReview({
          moduleId: filters.moduleId,
          topics: filters.topic ? [filters.topic] : undefined,
          difficulty: filters.difficulty || undefined,
          status: filters.status || undefined,
          skip: 0,
        })
          .then((fetchedQuestion) => {
            if (!cancelled && !fetchedQuestion) {
              showMessage('No question found matching the filters', 3000, 'error');
            }
            // Don't navigate - just fetch the question and keep the current URL
          })
          .catch((error) => {
            // Ignore abort errors - they're expected when requests are cancelled
            if (!cancelled) {
              const isAbortError =
                error?.name === 'AbortError' ||
                error?.message?.toLowerCase().includes('aborted') ||
                error?.message?.toLowerCase().includes('operation was aborted');
              if (!isAbortError) {
                const errorMessage = error instanceof Error ? error.message : 'Failed to fetch question';
                showMessage(errorMessage, 3000, 'error');
              }
            }
          })
          .finally(() => {
            if (!cancelled) {
              isFetchingRef.current = false;
            }
          });
      } else {
        // If we have a specific questionId (not 'question'), fetch that question using skip to find it
        // Skip fetch if already fetching or if we've already fetched for this questionId
        if (isFetchingRef.current || fetchedQuestionIdRef.current === questionId) {
          return;
        }

        isFetchingRef.current = true;
        fetchedQuestionIdRef.current = questionId;
        fetchQuestionForReview({
          moduleId: filters.moduleId,
          topics: filters.topic ? [filters.topic] : undefined,
          difficulty: filters.difficulty || undefined,
          status: filters.status || undefined,
          skip: skipCount,
        })
          .then((fetchedQuestion) => {
            // Verify the fetched question matches the questionId in URL
            if (!cancelled && fetchedQuestion && fetchedQuestion.id !== questionId) {
              // Question doesn't match - this can happen with skip, but we should log it
              console.warn(`Fetched question ID (${fetchedQuestion.id}) does not match URL questionId (${questionId})`);
            }
          })
          .catch((error) => {
            // Ignore abort errors - they're expected when requests are cancelled
            if (!cancelled) {
              const isAbortError =
                error?.name === 'AbortError' ||
                error?.message?.toLowerCase().includes('aborted') ||
                error?.message?.toLowerCase().includes('operation was aborted');
              if (!isAbortError) {
                const errorMessage = error instanceof Error ? error.message : 'Failed to fetch question';
                showMessage(errorMessage, 3000, 'error');
              }
            }
          })
          .finally(() => {
            if (!cancelled) {
              isFetchingRef.current = false;
            }
          });
      }
    }

    return () => {
      cancelled = true;
      isFetchingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.moduleId, filters.topic, filters.difficulty, filters.status, questionId, skipCount]);

  useEffect(() => {
    if (!filters.moduleId) {
      setQuestionCount(null);
      return;
    }

    let cancelled = false;

    countQuestions({
      moduleId: filters.moduleId,
      topics: filters.topic ? [filters.topic] : undefined,
      difficulties: filters.difficulty ? [filters.difficulty] : undefined,
      statuses: filters.status ? [filters.status] : undefined,
    })
      .then((count) => {
        if (!cancelled) {
          setQuestionCount(count);
        }
      })
      .catch((error) => {
        // Ignore errors if request was cancelled or if it's an abort error
        // Mutations shouldn't abort, but we keep this check for safety
        if (!cancelled) {
          const isAbortError =
            error?.name === 'AbortError' ||
            error?.message?.toLowerCase().includes('aborted') ||
            error?.message?.toLowerCase().includes('operation was aborted');
          if (!isAbortError) {
            console.error('Error fetching question count:', error);
          }
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.moduleId, filters.topic, filters.difficulty, filters.status]);

  const handleAnswerSelect = useCallback((answer: string) => {
    setSelectedAnswer(answer);
  }, []);

  const handleSubmitAnswer = useCallback(() => {
    if (!selectedAnswer) {
      showMessage('Please select an answer', 3000, 'error');
      return;
    }
    setShowFeedback(true);
  }, [selectedAnswer, showMessage]);

  const handleUpdateStatus = useCallback(
    async (status: QuestionStatus, statusNote?: string) => {
      if (!currentQuestion || !filters.moduleId) return;

      try {
        if (status === QuestionStatus.ERROR) {
          await flagQuestionAsError(filters.moduleId, currentQuestion.id, statusNote);
        } else if (status === QuestionStatus.OUT_OF_SCOPE) {
          await flagQuestionAsOutOfScope(filters.moduleId, currentQuestion.id, statusNote);
        } else {
          await changeQuestionStatus(filters.moduleId, currentQuestion.id, status, statusNote);
        }
        showMessage(`Question status updated to ${status}`, 3000, 'success');
        setSearchParam('skip', 0); // Reset skip count when status is updated
        const nextQuestion = await fetchQuestionForReview({
          moduleId: filters.moduleId,
          topics: filters.topic ? [filters.topic] : undefined,
          difficulty: filters.difficulty || undefined,
          status: filters.status || undefined,
          skip: 0,
        });
        if (nextQuestion) {
          // Preserve current URL search params (filters are already in URL)
          // location.search already includes the '?' prefix
          const searchString = location.search || '';
          navigate(`/admin/questionReview/${nextQuestion.id}${searchString}`, {
            replace: false,
          });
          setSelectedAnswer('');
          setShowFeedback(false);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to update question status';
        showMessage(errorMessage, 3000, 'error');
      }
    },
    [
      currentQuestion,
      filters,
      changeQuestionStatus,
      flagQuestionAsError,
      flagQuestionAsOutOfScope,
      fetchQuestionForReview,
      navigate,
      showMessage,
      location.search,
      setSearchParam,
    ],
  );

  const handleBack = useCallback(() => {
    navigate('/admin/questionReview');
  }, [navigate]);

  const handleReloadQuestion = useCallback(async () => {
    if (!filters.moduleId || !questionId) {
      showMessage('Module ID or Question ID is missing', 3000, 'error');
      return;
    }

    try {
      const reloadedQuestion = await fetchQuestionForReview({
        moduleId: filters.moduleId,
        topics: filters.topic ? [filters.topic] : undefined,
        difficulty: filters.difficulty || undefined,
        status: filters.status || undefined,
        skip: 0,
      });

      if (reloadedQuestion) {
        // Preserve current URL search params (filters are already in URL)
        // location.search already includes the '?' prefix
        const searchString = location.search || '';
        navigate(`/admin/questionReview/${reloadedQuestion.id}${searchString}`, {
          replace: true,
        });
        setSelectedAnswer('');
        setShowFeedback(false);
        showMessage('Question reloaded', 2000, 'success');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reload question';
      showMessage(errorMessage, 3000, 'error');
    }
  }, [filters, questionId, fetchQuestionForReview, navigate, showMessage, location.search]);

  const handleNextQuestion = useCallback(async () => {
    if (!filters.moduleId) {
      showMessage('Please select a module', 3000, 'error');
      return;
    }

    const newSkipCount = skipCount + 1;

    try {
      const nextQuestion = await fetchQuestionForReview({
        moduleId: filters.moduleId,
        topics: filters.topic ? [filters.topic] : undefined,
        difficulty: filters.difficulty || undefined,
        status: filters.status || undefined,
        skip: newSkipCount,
      });

      if (nextQuestion) {
        // Update skip in URL and navigate with the new search params (filters are already in URL)
        const currentParams = new URLSearchParams(location.search);
        currentParams.set('skip', String(newSkipCount));
        const searchString = currentParams.toString() ? `?${currentParams.toString()}` : '';
        navigate(`/admin/questionReview/${nextQuestion.id}${searchString}`, {
          replace: false,
        });
        setSelectedAnswer('');
        setShowFeedback(false);
      } else {
        showMessage('No more questions found', 3000, 'info');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch next question';
      showMessage(errorMessage, 3000, 'error');
    }
  }, [filters, fetchQuestionForReview, navigate, showMessage, skipCount, location]);

  const handlePrevQuestion = useCallback(async () => {
    if (!filters.moduleId) {
      showMessage('Please select a module', 3000, 'error');
      return;
    }

    const newSkipCount = Math.max(0, skipCount - 1);

    try {
      const prevQuestion = await fetchQuestionForReview({
        moduleId: filters.moduleId,
        topics: filters.topic ? [filters.topic] : undefined,
        difficulty: filters.difficulty || undefined,
        status: filters.status || undefined,
        skip: newSkipCount,
      });

      if (prevQuestion) {
        // Update skip in URL and navigate with the new search params (filters are already in URL)
        const currentParams = new URLSearchParams(location.search);
        if (newSkipCount > 0) {
          currentParams.set('skip', String(newSkipCount));
        } else {
          currentParams.delete('skip');
        }
        const searchString = currentParams.toString() ? `?${currentParams.toString()}` : '';
        navigate(`/admin/questionReview/${prevQuestion.id}${searchString}`, {
          replace: false,
        });
        setSelectedAnswer('');
        setShowFeedback(false);
      } else {
        showMessage('No more questions found', 3000, 'info');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch previous question';
      showMessage(errorMessage, 3000, 'error');
    }
  }, [filters, fetchQuestionForReview, navigate, showMessage, skipCount, location]);

  const isCorrect = Boolean(currentQuestion && selectedAnswer === currentQuestion.answer);

  const getNextWorkflowStep = useCallback((currentStatus: QuestionStatus): QuestionStatus | null => {
    const {workflow} = env.question;
    const currentIndex = workflow.indexOf(currentStatus);
    if (currentIndex === -1 || currentIndex === workflow.length - 1) {
      return null;
    }
    return workflow[currentIndex + 1];
  }, []);

  const nextStep = currentQuestion ? getNextWorkflowStep(currentQuestion.status) : null;

  const sessionQuestion = currentQuestion
    ? {
        ...currentQuestion,
        selectedAnswer: showFeedback ? selectedAnswer : undefined,
        isCorrect: showFeedback ? isCorrect : undefined,
      }
    : null;

  return {
    filters,
    selectedAnswer,
    showFeedback,
    questionCount,
    currentQuestion,
    fetchLoading,
    countLoading,
    updateLoading,
    isAdmin,
    isReviewer,
    isCorrect,
    nextStep,
    sessionQuestion,
    handleAnswerSelect,
    handleSubmitAnswer,
    handleUpdateStatus,
    handleBack,
    handleReloadQuestion,
    handleNextQuestion,
    handlePrevQuestion,
    skipCount,
    examModules,
    questionId,
  };
}
